import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import * as Highcharts from 'highcharts';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { AppState } from '@store/state/app.state';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { getMarketDetails } from '@store/selectors/market.selector';
import moment from 'moment';

@Component({
    selector: 'app-highchart',
    templateUrl: './highchart.component.html',
    styleUrls: ['./highchart.component.scss'],
    standalone: false
})
export class HighchartComponent implements OnInit, OnDestroy {

  unitsSelected = 'price';

  market: any;
  marketNS = '';

  highcharts = Highcharts;
  chartOptions = {};

  priceDays = [];
  priceDayValues = [];
  priceMonths = [];
  priceMonthValues = [];
  priceYears = [];
  priceYearValues = [];

  volumeDays = [];
  volumeDayValues = [];
  volumeMonths = [];
  volumeMonthValues = [];
  volumeYears = [];
  volumeYearValues = [];

  zoomSelected = 'month';

  @Output() minValue = new EventEmitter();
  @Output() maxValue = new EventEmitter();

  priceMinDay = 9999;
  priceMaxDay = -9999;
  priceMinMonth = 9999;
  priceMaxMonth = -9999;
  priceMinYear = 9999;
  priceMaxYear = -9999;

  volumeMinDay = 9999;
  volumeMaxDay = -9999;
  volumeMinMonth = 9999;
  volumeMaxMonth = -9999;
  volumeMinYear = 9999;
  volumeMaxYear = -9999;

  private unsubscribe$: Subject<void> = new Subject();

  constructor(private readonly activatedRoute: ActivatedRoute,
              public store: Store<AppState>) {
    this.marketNS = this.activatedRoute.snapshot.params.id;
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe(
      params => {
        this.marketNS = params.id;
        this.fillValuesByCountry('month');
      }
    );

    this.minValue.emit(this.priceMinMonth);
    this.maxValue.emit(this.priceMaxMonth);
  }

  fillValuesByCountry(zoom) {

    this.store.pipe(select(getMarketDetails), takeUntil(this.unsubscribe$)).subscribe(marketDetails => {
      const priceDays = [];
      const priceDayValues = [];
      let priceMonths = [];
      const priceMonthValues = [];
      let priceYears = [];
      const priceYearValues = [];

      const volumeDays = [];
      const volumeDayValues = [];
      let volumeMonths = [];
      const volumeMonthValues = [];
      let volumeYears = [];
      const volumeYearValues = [];

      if (marketDetails) {
        this.market = marketDetails.filter(x => x.ns === this.marketNS)[0];
        const marketHistory = this.market;

        // PRICE HISTORY
        if (marketHistory.ph !== undefined && marketHistory.ph !== null) {
          if (marketHistory.ph.zd !== undefined && marketHistory.ph.zd !== null) {
            for (const days of marketHistory.ph.zd) {
              const formatedValue = Number(days.value).toFixed(2);
              if (marketHistory.sc === 'OMIE') {
                const createdAt = moment(days.value_date, 'YYYY-MM-DD HH:mm:ss');
                const dayValue = moment(createdAt).format('HH:mm');
                const dayMonth = moment(createdAt).format('DMMM');
                const year = moment(createdAt).format('Y').slice(-2);
                priceDays.push(dayMonth + year  + ',' + dayValue);
              } else {
                const createdAt = moment(days.value_date, 'YYYY-MM-DD HH:mm:ss');
                const dayMonth = moment(createdAt).format('DMMM');
                const year = moment(createdAt).format('Y').slice(-2);
                priceDays.push(dayMonth + year);
              }
              priceDayValues.push(Number(formatedValue));
            }

            for (const yVal of priceDayValues) {
              if (yVal < this.priceMinDay) {
                this.priceMinDay = yVal;
              }
              if (yVal > this.priceMaxDay) {
                this.priceMaxDay = yVal;
              }
            }
          }

          if (marketHistory.ph.zm !== undefined && marketHistory.ph.zm !== null) {
            for (const months of marketHistory.ph.zm) {
              const formatedValue = Number(months.value).toFixed(2);
              priceMonths.push(months.value_date);
              priceMonthValues.push(Number(formatedValue));
            }

            for (const yVal of priceMonthValues) {
              if (yVal < this.priceMinMonth) {
                this.priceMinMonth = yVal;
              }
              if (yVal > this.priceMaxMonth) {
                this.priceMaxMonth = yVal;
              }
            }
            const priceMonthsTemp = [];

            for (const day of priceMonths) {
              let createdAt = moment(day, 'YYYY-MM-DD');
              if (!createdAt.isValid()) {
                createdAt = moment(day, 'YYYY-MM-DD HH:mm:ss');
              }
              if (createdAt.isValid()) {
                const dayMonth = moment(createdAt).format('DMMM');
                const year = moment(createdAt).format('Y').slice(-2);
                priceMonthsTemp.push(dayMonth + year);
              } else {
                priceMonthsTemp.push(day);
              }
            }
            priceMonths = priceMonthsTemp;

          }

          if (marketHistory.ph.zy !== undefined && marketHistory.ph.zy !== null) {
            for (const years of marketHistory.ph.zy) {
              const formatedValue = Number(years.value).toFixed(2);
              priceYears.push(years.value_date);
              priceYearValues.push(Number(formatedValue));
            }

            for (const yVal of priceYearValues) {
              if (yVal < this.priceMinYear) {
                this.priceMinYear = yVal;
              }
              if (yVal > this.priceMaxYear) {
                this.priceMaxYear = yVal;
              }
            }

            const priceYearsTemp = [];

            for (const day of priceYears) {
              let createdAt = moment(day, 'YYYY-MM-DD');
              if (!createdAt.isValid()) {
                createdAt = moment(day, 'YYYY-MM-DD HH:mm:ss');
              }
              const dayMonth = moment(createdAt).format('DMMM');
              const year = moment(createdAt).format('Y').slice(-2);
              priceYearsTemp.push(dayMonth + year);
            }
            priceYears = priceYearsTemp;
          }
        }

        // VOLUME HISTORY
        if (marketHistory.vh !== undefined && marketHistory.vh !== null) {
          if (marketHistory.vh.zd !== undefined && marketHistory.vh.zd !== null) {
            for (const days of marketHistory.vh.zd) {
              const formatedValue = Number(days.value).toFixed(2);
              if (marketHistory.sc === 'OMIE') {
                const createdAt = moment(days.value_date, 'YYYY-MM-DD HH:mm:ss');
                const dayValue = moment(createdAt).format('HH:mm');
                const dayMonth = moment(createdAt).format('DMMM');
                const year = moment(createdAt).format('Y').slice(-2);
                volumeDays.push(dayMonth + year  + ',' + dayValue);
              } else {
                const createdAt = moment(days.value_date, 'YYYY-MM-DD HH:mm:ss');
                const dayMonth = moment(createdAt).format('DMMM');
                const year = moment(createdAt).format('Y').slice(-2);
                volumeDays.push(dayMonth + year);
              }
              volumeDayValues.push(Number(formatedValue));
            }

            for (const yVal of volumeDayValues) {
              if (yVal < this.volumeMinDay) {
                this.volumeMinDay = yVal;
              }
              if (yVal > this.volumeMaxDay) {
                this.volumeMaxDay = yVal;
              }
            }
          }

          if (marketHistory.vh.zd !== undefined && marketHistory.vh.zd !== null) {
            for (const months of marketHistory.vh.zm) {
              const formatedValue = Number(months.value).toFixed(2);
              volumeMonths.push(months.value_date);
              volumeMonthValues.push(Number(formatedValue));
            }

            for (const yVal of volumeMonthValues) {
              if (yVal < this.volumeMinMonth) {
                this.volumeMinMonth = yVal;
              }
              if (yVal > this.volumeMaxMonth) {
                this.volumeMaxMonth = yVal;
              }
            }
            const volumeMonthsTemp = [];

            for (const day of volumeMonths) {
              const createdAt = moment(day, 'YYYY-MM-DD');
              const dayMonth = moment(createdAt).format('DMMM');
              const year = moment(createdAt).format('Y').slice(-2);
              volumeMonthsTemp.push(dayMonth + year);
            }
            volumeMonths = volumeMonthsTemp;

          }

          if (marketHistory.vh.zy !== undefined && marketHistory.vh.zy !== null) {
            for (const years of marketHistory.vh.zy) {
              const formatedValue = Number(years.value).toFixed(2);
              volumeYears.push(years.value_date);
              volumeYearValues.push(Number(formatedValue));
            }

            for (const yVal of volumeYearValues) {
              if (yVal < this.volumeMinYear) {
                this.volumeMinYear = yVal;
              }
              if (yVal > this.volumeMaxYear) {
                this.volumeMaxYear = yVal;
              }
            }

            const volumeYearsTemp = [];

            for (const day of volumeYears) {
              const createdAt = moment(day, 'YYYY-MM-DD');
              const dayMonth = moment(createdAt).format('DMMM');
              const year = moment(createdAt).format('Y').slice(-2);
              volumeYearsTemp.push(dayMonth + year);
            }
            volumeYears = volumeYearsTemp;
          }
        }

        this.priceDays = priceDays;
        this.priceDayValues = priceDayValues;
        this.priceMonths = priceMonths;
        this.priceMonthValues = priceMonthValues;
        this.priceYears = priceYears;
        this.priceYearValues = priceYearValues;

        this.volumeDays = volumeDays;
        this.volumeDayValues = volumeDayValues;
        this.volumeMonths = volumeMonths;
        this.volumeMonthValues = volumeMonthValues;
        this.volumeYears = volumeYears;
        this.volumeYearValues = volumeYearValues;

        if (zoom === 'day') {
          if (marketHistory.sc === 'OMIE') {
            this.updateChartOptions(200, 4, priceDays, priceDayValues, marketHistory.ct);
          } else {
            this.updateChartOptions(200, 7, priceDays, priceDayValues, marketHistory.ct);
          }
        } else if (zoom === 'month') {
          if (marketHistory.sc === 'OMIE') {
            this.updateChartOptions(200, 7, priceMonths, priceMonthValues, marketHistory.ct);
          } else {
            this.updateChartOptions(2000, 7, priceMonths, priceMonthValues, marketHistory.ct);
          }
        } else {
          if (marketHistory.sc === 'OMIE') {
            this.updateChartOptions(3000, 7, priceYears, priceYearValues, marketHistory.ct);
          } else {
            this.updateChartOptions(200, 7, priceYears, priceYearValues, marketHistory.ct);
          }
        }
      }

    });
  }

  setDayZoom() {
    this.zoomSelected = 'day';
    if (this.unitsSelected === 'volume') {
      this.updateXY(this.volumeDays, this.volumeDayValues);
    } else {
      this.updateXY(this.priceDays, this.priceDayValues);
    }
  }

  setMonthZoom() {
    this.zoomSelected = 'month';
    if (this.unitsSelected === 'volume') {
      this.updateXY(this.volumeMonths, this.volumeMonthValues);
    } else {
      this.updateXY(this.priceMonths, this.priceMonthValues);
    }
  }

  setYearZoom() {
    this.zoomSelected = 'year';
    if (this.unitsSelected === 'volume') {
      this.updateXY(this.volumeYears, this.volumeYearValues);
    } else {
      this.updateXY(this.priceYears, this.priceYearValues);
    }
  }

  changeUnitsGraph(units) {
    this.unitsSelected = units;
    if (this.zoomSelected === 'day') {
      this.setDayZoom();
    } else if (this.zoomSelected === 'month') {
      this.setMonthZoom();
    } else {
      this.setYearZoom();
    }
  }

  updateXY(x, y) {
    if (this.zoomSelected === 'day') {
      if (this.unitsSelected === 'price') {
        this.minValue.emit(this.priceMinDay);
        this.maxValue.emit(this.priceMaxDay);
      } else {
        this.minValue.emit(this.volumeMinDay);
        this.maxValue.emit(this.volumeMaxDay);
      }
    } else if (this.zoomSelected === 'month') {
      if (this.unitsSelected === 'price') {
        this.minValue.emit(this.priceMinMonth);
        this.maxValue.emit(this.priceMaxMonth);
      } else {
        this.minValue.emit(this.volumeMinMonth);
        this.maxValue.emit(this.volumeMaxMonth);
      }
    } else {
      if (this.unitsSelected === 'price') {
        this.minValue.emit(this.priceMinYear);
        this.maxValue.emit(this.priceMaxYear);
      } else {
        this.minValue.emit(this.volumeMinYear);
        this.maxValue.emit(this.volumeMaxYear);
      }
    }

    if (this.market.sc === 'OMIE') {
      if (this.zoomSelected === 'day') {
        this.updateChartOptions(800, 4, x, y, this.market.ct);
      } else if (this.zoomSelected === 'month') {
        this.updateChartOptions(800, 7, x, y, this.market.ct);
      } else {
        this.updateChartOptions(3000, 7, x, y, this.market.ct);
      }
    } else {
      if (this.zoomSelected === 'day') {
        this.updateChartOptions(800, 7, x, y, this.market.ct);
      } else if (this.zoomSelected === 'month') {
        this.updateChartOptions(2000, 7, x, y, this.market.ct);
      } else {
        this.updateChartOptions(800, 7, x, y, this.market.ct);
      }
    }

    if (this.zoomSelected === 'day') {
      this.highcharts.charts[this.highcharts.charts.length - 1].xAxis[0].setExtremes(0, this.priceDays.length - 1);
    } else if (this.zoomSelected === 'month') {
      this.highcharts.charts[this.highcharts.charts.length - 1].xAxis[0].setExtremes(0, this.priceMonths.length - 1);
    } else {
      this.highcharts.charts[this.highcharts.charts.length - 1].xAxis[0].setExtremes(0, this.priceYears.length - 1);
    }
  }

  setCountry(country) {
    this.marketNS = country;
    this.fillValuesByCountry(this.zoomSelected);
  }

  updateChartOptions(val, steps, xValues, yValues, country) {

    let gradient: any;
    let lineColor: any;

    if (country === 'MS') {
      gradient = {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, '#3A75F5'],
          [1, '#e6f2ff']
        ]
      };
      lineColor = '#3A75F5';
    } else {
      gradient = {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, '#C5FFBCCA'],
          [1, '#28FF5200']
        ]
      };
      lineColor = '#28FF52';
    }

    if (val === 800) {
      this.chartOptions = {
        title: {
          text: ''
        },
        chart: {
            type: 'area',
            panning: true,
            pinchType: 'x',
            zoomType: null,
            resetZoomButton: {
              theme: {
                  display: 'none'
              }
            },
            backgroundColor: '#FAFAFA',
            events: {
              load() {
                const xx = (-1) * (this.yAxis[0].translate(0, false, false));
                this.xAxis[0].axisGroup.translate(0, xx);
                this.xAxis[0].labelGroup.translate(0, xx);
              },
              redraw() {
                const xx = (-1) * (this.yAxis[0].translate(0, false, false));
                this.xAxis[0].axisGroup.translate(0, xx);
                this.xAxis[0].labelGroup.translate(0, xx);
              }
            },
            scrollablePlotArea: {
              minWidth: 200,
              scrollPositionX: 0
          }
        },
        legend: {
          text: '',
          layout: 'vertical',
          floating: true,
          align: 'left',
          x: 100,
          y: 70
        },
        tooltip: {
          useHTML: true,
          outside: false,
          formatter() {
            if (this.x.split(',').length > 1) {
              return '<table><tr><td style="text-align: center;"><b>' + this.y +
              ' <span style="font-size: 8px;">€/MWh</span></b></td></tr><tr><td style="text-align: center;">' + this.x.split(',')[0] +
              '</td></tr><tr><td style="text-align: center;">' +  this.x.split(',')[1] + '</td></tr></table>';
            } else {
              return '<table><tr><td style="text-align: center;"><b>' + this.y +
              ' <span style="font-size: 8px;">€/MWh</span></b></td></tr><tr><td style="text-align: center;">' + this.x.split(',')[0] +
              '</td></tr></table>';
            }
          },
          followPointer: false,
          followTouchMove: false,
          style: {
            fontSize: '10px'
          }
        },
        xAxis: {
          categories: xValues,
          offset: -5,
          title: {
            text: ''
          },
          labels: {
            rotation: 0,
            style: {
                color: '#616161',
                fontSize: '10px',
                height: '16px',
                fontFamily: 'Mulish',
                fontWeight: '400',
                letterSpacing: '1.17px',
                lineHeight: '16px',
                maxWidth: '20px'
            },
            step: steps,
            formatter() {
              if (typeof this.value.includes === 'function' && this.value.includes(',')) {
                return this.value.split(',')[1];
              } else {
                return this.value;
              }
            }
          }
        },
        yAxis: {
            title: {
                text: ''
            },
            gridLineColor: '#EDEDED',
            plotLines: [{
              color: '#FAFAFA',
              width: 2,
              value: 0
            }],
            labels: {
              style: {
                  color: '#AFAFAF',
                  height: '16px',
                  fontFamily: 'Mulish',
                  fontSize: '10px',
                  fontWeight: '700',
                  letterSpacing: '1.17px',
                  lineHeight: '16px'
              },
              align: 'left',
              x: 0,
              y: -2
            }
        },
        plotOptions: {
            series: {
                marker: {
                  fillColor: '#28FF52',
                  radius: 3,
                  lineColor: '#28FF52'
                }
            }
        },
        series: [{
            data: yValues,
            color: lineColor,
            fillColor: gradient,
            name: ''
        }],
        credits: {
          enabled: false
        }
      };
    } else {
      this.chartOptions = {
        title: {
          text: ''
        },
        chart: {
            type: 'area',
            panning: true,
            pinchType: 'x',
            zoomType: null,
            resetZoomButton: {
              theme: {
                  display: 'none'
              }
            },
            backgroundColor: '#FAFAFA',
            events: {
              load() {
                const xx = (-1) * (this.yAxis[0].translate(0, false, false));
                this.xAxis[0].axisGroup.translate(0, xx);
                this.xAxis[0].labelGroup.translate(0, xx);
              },
              redraw() {
                const xx = (-1) * (this.yAxis[0].translate(0, false, false));
                this.xAxis[0].axisGroup.translate(0, xx);
                this.xAxis[0].labelGroup.translate(0, xx);
              }
            },
            scrollablePlotArea: {
              minWidth: val,
              scrollPositionX: 0
          }
        },
        legend: {
          text: '',
          layout: 'vertical',
          floating: true,
          align: 'left',
          x: 100,
          y: 70
        },
        tooltip: {
          useHTML: true,
          outside: false,
          formatter() {
            if (this.x.split(',').length > 1) {
              return '<table><tr><td style="text-align: center;"><b>' + this.y +
              ' <span style="font-size: 8px;">€/MWh</span></b></td></tr><tr><td style="text-align: center;">' + this.x.split(',')[0] +
              '</td></tr><tr><td style="text-align: center;">' +  this.x.split(',')[1] + '</td></tr></table>';
            } else {
              return '<table><tr><td style="text-align: center;"><b>' + this.y +
              ' <span style="font-size: 8px;">€/MWh</span></b></td></tr><tr><td style="text-align: center;">' + this.x.split(',')[0] +
              '</td></tr></table>';
            }
          },
          followPointer: false,
          followTouchMove: false,
          style: {
            fontSize: '10px'
          }
        },
        xAxis: {
          categories: xValues,
          offset: -5,
          title: {
            text: ''
          },
          labels: {
            rotation: 0,
            style: {
                color: '#616161',
                fontSize: '10px',
                height: '16px',
                fontFamily: 'Mulish',
                fontWeight: '400',
                letterSpacing: '1.17px',
                lineHeight: '16px'
            },
            step: steps,
            formatter() {
              if (typeof this.value.includes === 'function' && this.value.includes(',')) {
                return this.value.split(',')[1];
              } else {
                return this.value;
              }
            }
          }
        },
        yAxis: {
            title: {
                text: ''
            },
            gridLineColor: '#EDEDED',
            plotLines: [{
              color: '#FAFAFA',
              width: 2,
              value: 0
            }],
            labels: {
              style: {
                  color: '#AFAFAF',
                  height: '16px',
                  fontFamily: 'Mulish',
                  fontSize: '10px',
                  fontWeight: '700',
                  letterSpacing: '1.17px',
                  lineHeight: '16px'
              },
              align: 'left',
              x: 0,
              y: -2
            }
        },
        plotOptions: {
            series: {
                marker: {
                  fillColor: '#28FF52',
                  radius: 3,
                  lineColor: '#28FF52'
                }
            }
        },
        series: [{
            data: yValues,
            color: lineColor,
            fillColor: gradient,
            name: ''
        }],
        credits: {
          enabled: false
        }
      };
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
